import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaArrowLeft, 
  FaUserPlus, 
  FaUserCheck, 
  FaEllipsisH,
  FaHeart,
  FaComment,
  FaShare,
  FaBookmark,
  FaPlay
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import PostCard from '../components/PostCard';
import AppBar from '../components/AppBar';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
`;

const Header = styled.div`
  background-color: #111;
  padding: 20px;
  border-bottom: 1px solid #333;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #333;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  color: #fff;
  font-size: 20px;
  font-weight: bold;
  margin: 0;
`;

const PostCount = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-top: 4px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  background-color: ${props => props.primary ? '#4CAF50' : 'transparent'};
  color: ${props => props.primary ? '#fff' : '#4CAF50'};
  border: 1px solid #4CAF50;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${props => props.primary ? '#45a049' : '#4CAF50'};
    color: #fff;
  }
`;

const OptionsButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }
`;

const ProfileSection = styled.div`
  padding: 32px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProfileHeader = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProfileImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #4CAF50;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h2`
  color: #fff;
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ProfileUsername = styled.div`
  color: #ccc;
  font-size: 16px;
  margin-bottom: 16px;
`;

const ProfileBio = styled.div`
  color: #fff;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
  max-width: 600px;
`;

const ProfileStats = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  color: #fff;
  font-size: 20px;
  font-weight: bold;
`;

const StatLabel = styled.div`
  color: #ccc;
  font-size: 14px;
`;

const ProfileActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const AdditionalInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const InfoItem = styled.div`
  color: #ccc;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid #333;
  margin-bottom: 24px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0;
`;

const Tab = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? '#4CAF50' : '#666'};
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#4CAF50' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.active ? '#4CAF50' : '#fff'};
  }
`;

const PostsContainer = styled.div`
  display: grid;
  gap: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  color: #4CAF50;
`;

const EmptyStateText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 16px;
  color: #ccc;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #4CAF50;
  font-size: 18px;
`;

const UserProfileScreen = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading user data
    setTimeout(() => {
      setUser({
        id: userId,
        username: 'johndoe',
        meta: {
          name: 'John Doe',
          profile_image: '/default-avatar.png',
          bio: 'Passionate developer and content creator. Love sharing knowledge and building amazing things! 🚀',
          occupation: 'Software Developer',
          location: 'San Francisco, CA'
        },
        stats: {
          posts: 42,
          followers: 1234,
          followings: 567
        }
      });
      setLoading(false);
    }, 1000);
  }, [userId]);

  useEffect(() => {
    if (user) {
      // Simulate loading posts
      setPosts([
        {
          id: 1,
          user: {
            id: user.id,
            username: user.username,
            meta: {
              name: user.meta.name,
              profile_image: user.meta.profile_image
            }
          },
          content: 'Just finished building an amazing new feature! The team worked really hard on this one. #coding #development',
          created_at: '2024-01-15T10:30:00Z',
          likes_count: 24,
          comments_count: 8,
          is_liked: false,
          media: [
            {
              path: 'https://picsum.photos/600/400',
              type: 'image'
            }
          ]
        },
        {
          id: 2,
          user: {
            id: user.id,
            username: user.username,
            meta: {
              name: user.meta.name,
              profile_image: user.meta.profile_image
            }
          },
          content: 'Beautiful sunset today! Nature never fails to amaze me. 🌅',
          created_at: '2024-01-14T18:45:00Z',
          likes_count: 56,
          comments_count: 12,
          is_liked: true,
          media: [
            {
              path: 'https://picsum.photos/600/500',
              type: 'image'
            }
          ]
        }
      ]);
    }
  }, [user]);

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    toast.success(isFollowing ? 'Unfollowed' : 'Followed!');
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading profile...</LoadingContainer>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateIcon>
            <FaUserPlus />
          </EmptyStateIcon>
          <EmptyStateText>User not found</EmptyStateText>
          <EmptyStateSubtext>The user you're looking for doesn't exist</EmptyStateSubtext>
        </EmptyState>
      </Container>
    );
  }

  const isOwnProfile = currentUser?.id === user.id;

  return (
    <Container>
      <AppBar />
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBack}>
            <FaArrowLeft />
          </BackButton>
          <HeaderInfo>
            <UserName>{user.meta.name}</UserName>
            <PostCount>{user.stats.posts} posts</PostCount>
          </HeaderInfo>
          <HeaderActions>
            {!isOwnProfile && (
              <ActionButton 
                primary={!isFollowing}
                onClick={handleFollow}
              >
                {isFollowing ? <FaUserCheck /> : <FaUserPlus />}
                {isFollowing ? 'Following' : 'Follow'}
              </ActionButton>
            )}
            <OptionsButton>
              <FaEllipsisH />
            </OptionsButton>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <ProfileSection>
        <ProfileHeader>
          <ProfileImage src={user.meta.profile_image} alt={user.meta.name} />
          <ProfileInfo>
            <ProfileName>{user.meta.name}</ProfileName>
            <ProfileUsername>@{user.username}</ProfileUsername>
            <ProfileBio>{user.meta.bio}</ProfileBio>
            
            <ProfileStats>
              <StatItem>
                <StatNumber>{user.stats.posts}</StatNumber>
                <StatLabel>Posts</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{user.stats.followers}</StatNumber>
                <StatLabel>Followers</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{user.stats.followings}</StatNumber>
                <StatLabel>Following</StatLabel>
              </StatItem>
            </ProfileStats>

            <AdditionalInfo>
              {user.meta.occupation && (
                <InfoItem>
                  💼 {user.meta.occupation}
                </InfoItem>
              )}
              {user.meta.location && (
                <InfoItem>
                  📍 {user.meta.location}
                </InfoItem>
              )}
            </AdditionalInfo>
          </ProfileInfo>
        </ProfileHeader>

        <TabsContainer>
          <Tabs>
            <Tab 
              active={activeTab === 'posts'} 
              onClick={() => setActiveTab('posts')}
            >
              Posts
            </Tab>
            <Tab 
              active={activeTab === 'media'} 
              onClick={() => setActiveTab('media')}
            >
              Media
            </Tab>
            <Tab 
              active={activeTab === 'likes'} 
              onClick={() => setActiveTab('likes')}
            >
              Likes
            </Tab>
          </Tabs>
        </TabsContainer>

        {activeTab === 'posts' && (
          posts.length > 0 ? (
            <PostsContainer>
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </PostsContainer>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <FaHeart />
              </EmptyStateIcon>
              <EmptyStateText>No posts yet</EmptyStateText>
              <EmptyStateSubtext>When {user.meta.name} shares something, it will appear here</EmptyStateSubtext>
            </EmptyState>
          )
        )}

        {activeTab === 'media' && (
          <EmptyState>
            <EmptyStateIcon>
              <FaPlay />
            </EmptyStateIcon>
            <EmptyStateText>No media yet</EmptyStateText>
            <EmptyStateSubtext>When {user.meta.name} shares photos or videos, they will appear here</EmptyStateSubtext>
          </EmptyState>
        )}

        {activeTab === 'likes' && (
          <EmptyState>
            <EmptyStateIcon>
              <FaHeart />
            </EmptyStateIcon>
            <EmptyStateText>No liked posts</EmptyStateText>
            <EmptyStateSubtext>Posts that {user.meta.name} has liked will appear here</EmptyStateSubtext>
          </EmptyState>
        )}
      </ProfileSection>
    </Container>
  );
};

export default UserProfileScreen; 